/**
 * Pre-fills the native quantity selector of the product form when a visitor
 * picks a tier ("1 / 2 / 3 gommages") in the .tier-offer-selector block,
 * flags whether the free-gift bundle should apply, and lets the customer
 * pick a DIFFERENT scrub for their 2nd item on tiers 2/3 instead of being
 * forced to buy two units of the current product.
 *
 * IMPORTANT: tiers 2 and 3 both need 2 eligible units total - only tier 3
 * also wants the free "Milk 2 Glow Collagène" gift (a different product,
 * added separately by assets/free-gift-bundle.js). Since both tiers would
 * otherwise produce an identical cart line for the current product, the
 * gift intent cannot be inferred from quantity alone: this script writes it
 * as a cart-level attribute ("_tier_gift_intent") on the add-to-cart form,
 * which free-gift-bundle.js reads once the item is actually in the cart.
 *
 * Same idea for the 2nd item: if the customer chooses a different scrub in
 * the "2ème parfum" dropdown, the form only adds 1 unit of the CURRENT
 * product, and the chosen variant id is recorded as the cart-level attribute
 * "_tier_second_variant_id" - assets/second-scrub-bundle.js reads it after
 * the add and adds that other product separately. Cart-level attributes
 * (not line-item properties) are used on purpose so switching tiers or
 * scents never splits the product into extra duplicate cart lines.
 */

const TIER_CONFIG = {
  1: { qty: 1, gift: false, allowSecondPick: false },
  2: { qty: 2, gift: false, allowSecondPick: true },
  3: { qty: 2, gift: true, allowSecondPick: true },
};

const GIFT_INTENT_INPUT_NAME = 'attributes[_tier_gift_intent]';
const SECOND_VARIANT_INPUT_NAME = 'attributes[_tier_second_variant_id]';

function applyQuantity(quantitySelector, qty) {
  if (!quantitySelector) return;
  if (typeof quantitySelector.setValue === 'function') {
    quantitySelector.setValue(String(qty));
  }
  if (typeof quantitySelector.updateButtonStates === 'function') {
    quantitySelector.updateButtonStates();
  }
}

function setGiftIntent(formComponent, wantsGift) {
  if (!formComponent) return;
  const form = formComponent.querySelector('form');
  if (!form) return;
  let input = form.querySelector('[data-tier-gift-intent]');
  if (!input) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = GIFT_INTENT_INPUT_NAME;
    input.dataset.tierGiftIntent = 'true';
    form.appendChild(input);
  }
  input.value = wantsGift ? 'true' : 'false';
}

function setSecondVariantIntent(formComponent, variantId) {
  if (!formComponent) return;
  const form = formComponent.querySelector('form');
  if (!form) return;
  let input = form.querySelector('[data-tier-second-variant]');
  if (!input) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = SECOND_VARIANT_INPUT_NAME;
    input.dataset.tierSecondVariant = 'true';
    form.appendChild(input);
  }
  input.value = variantId || '';
}

function applyState(container, formComponent, quantitySelector) {
  const radio = container.querySelector('input[type="radio"]:checked');
  if (!radio) return;

  const config = TIER_CONFIG[Number(radio.value)];
  if (!config) return;

  const secondPickWrapper = container.querySelector('[data-tier-second-pick]');
  const secondSelect = container.querySelector('[data-tier-second-select]');

  if (secondPickWrapper) secondPickWrapper.hidden = !config.allowSecondPick;

  const secondVariantId = config.allowSecondPick && secondSelect ? secondSelect.value : '';
  const qty = secondVariantId ? 1 : config.qty;

  applyQuantity(quantitySelector, qty);
  setGiftIntent(formComponent, config.gift);
  setSecondVariantIntent(formComponent, secondVariantId);
}

function initTierOfferSelector(container) {
  if (container.dataset.tierOfferInit === 'true') return;
  container.dataset.tierOfferInit = 'true';

  const productId = container.dataset.productId;
  const formComponent = document.querySelector(`product-form-component[data-product-id="${productId}"]`);
  if (!formComponent) return;

  const quantitySelector = formComponent.querySelector('quantity-selector-component');
  const quantityWrapper = formComponent.querySelector('.quantity-selector-wrapper');

  // The tier cards replace the default +/- quantity stepper visually.
  if (quantityWrapper) quantityWrapper.hidden = true;

  const radios = container.querySelectorAll('input[type="radio"]');
  radios.forEach((radio) => {
    radio.addEventListener('change', () => {
      applyState(container, formComponent, quantitySelector);
    });
  });

  const secondSelect = container.querySelector('[data-tier-second-select]');
  if (secondSelect) {
    secondSelect.addEventListener('change', () => {
      applyState(container, formComponent, quantitySelector);
    });
  }

  applyState(container, formComponent, quantitySelector);
}

function initAll() {
  document.querySelectorAll('[data-tier-offer-selector]').forEach(initTierOfferSelector);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}
